import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setUsers } from '../../contextRedux/User/UserStore';
import { resetUserGroup, updateProps } from '../../contextRedux/UserGroup/UserGroupModel';
import { setUserGroups } from '../../contextRedux/UserGroup/UserGroupStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { UserGroup } from '../../DTOS/UserGroup/Response/UserGroup';
import UserService from '../../services/user/UserService';
import UserGroupService from '../../services/userGroup/UserGroupService';
import { RootState } from '../../store';
import UserGroupModal from './UserGroupModal/UserGroupModal';
import UserGroupTable from './UserGroupTable/UserGroupTable';

const UserGroupPage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [isParamsInitialized, setIsParamsInitialized] = useState(false);

  const reduxUserGroup = useSelector((state: RootState) => state.models.userGroup);
  const { skip, take } = reduxUserGroup.props;

  const parsed = queryString.parse(location.search);

  useEffect(() => {
    if (parsed.searchTerm && !isParamsInitialized) {
      setSearchTerm(parsed.searchTerm as string);
      setIsParamsInitialized(true);
    } else if (!parsed.searchTerm && !isParamsInitialized) {
      setIsParamsInitialized(true);
    }
  }, [parsed.searchTerm, isParamsInitialized]);

  useEffect(() => {
    const query = {
      ...parsed,
      searchTerm: searchTerm || undefined,
    };

    if (!searchTerm) {
      delete query.searchTerm;
    }

    const stringified = queryString.stringify(query, { skipEmptyString: true });
    navigate(`${location.pathname}?${stringified}`, { replace: true });
  }, [searchTerm, navigate, location.pathname]);

  useEffect(() => {
    dispatch(
      updateProps({
        field: 'searchTerm',
        value: searchTerm,
      })
    );
  }, [searchTerm, dispatch]);

  const {
    data: _dates,
    isFetching: isUserGroupsLoading,
    isError: isErrorOnFetchUserGroups,
    error: userGroupsError,
    refetch,
  } = useQuery({
    queryKey: ['userGroups', skip, take, searchTerm],
    queryFn: () =>
      UserGroupService.search({
        skip,
        take,
        termen: searchTerm || undefined,
      }).then((data: ControllerResponse<UserGroup[]>) => {
        dispatch(setUserGroups(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () =>
      UserService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetUserGroup({
        isModalVisible: true,
        refresh: true,
        termen: searchTerm || undefined,
        skip: searchTerm ? 1 : skip,
        take,
      })
    );
  };

  if (isUsersLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isUserGroupsLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchUserGroups) {
    return <Alert message={userGroupsError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Grup Afiliat Utilizatori</h1>
      <AnimateButton onClick={() => handleReset()}>Adauga</AnimateButton>
      <UserGroupModal />
      <UserGroupTable />
    </>
  );
};

export default UserGroupPage;
